// Full-page ask thread (spec 16 expand affordance).
//
// Read-only view. The palette is the *input* surface; this page is
// where an expanded thread breathes (right rail for entity cards,
// scannable prior turns).
//
// Continuation goes back through the palette: a small "Continue the
// thread" link dispatches a global event that AppShell picks up and
// opens the palette pre-loaded with this thread as prior_turns.
//
// URL is the truth: `?job=<id>` is the only state. The fetched job's
// `input.prior_turns` reconstructs the conversation.

import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { ArrowLeft, CornerDownRight } from "lucide-react";

import { Canvas } from "@/components/canvas/canvas";
import { GlassPanel } from "@/components/glass";
import { Prose } from "@/components/canvas/prose";
import {
	type AskResult,
	type CanvasPlan,
	defaultCanvas,
	validateCanvas,
} from "@/components/canvas/types";

interface PriorTurn {
	question: string;
	answer: string;
}

interface JobSnapshot {
	id: string;
	status: "pending" | "running" | "completed" | "failed" | "cancelled";
	input?: { question?: string; prior_turns?: PriorTurn[] };
	result?: unknown;
	error?: string | null;
	createdAt?: string;
	type?: string;
}

export default function AskView() {
	const { id: projectId } = useParams<{ id: string }>();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const [thread, setThread] = useState<PriorTurn[]>([]);
	const [question, setQuestion] = useState<string | null>(null);
	const [jobId, setJobId] = useState<string | null>(null);
	const [result, setResult] = useState<AskResult | null>(null);
	const [error, setError] = useState<string | null>(null);

	const jobParam = searchParams.get("job");

	useEffect(() => {
		if (!projectId || !jobParam) {
			setQuestion(null);
			setJobId(null);
			setResult(null);
			setThread([]);
			return;
		}
		let cancelled = false;
		fetch(
			`/api/projects/${encodeURIComponent(projectId)}/jobs/${encodeURIComponent(jobParam)}`,
			{ credentials: "include" },
		)
			.then((r) => (r.ok ? r.json() : null))
			.then((body) => {
				if (cancelled || !body) return;
				const j = body.job as JobSnapshot;
				if (j.status !== "completed") {
					setError(`ask is ${j.status}`);
					return;
				}
				setThread(j.input?.prior_turns ?? []);
				setQuestion(j.input?.question ?? null);
				setJobId(j.id);
				setResult((j.result as AskResult) ?? null);
				setError(null);
			})
			.catch((e) => {
				if (!cancelled) {
					setError(e instanceof Error ? e.message : String(e));
				}
			});
		return () => {
			cancelled = true;
		};
	}, [projectId, jobParam]);

	function focusNodeInGraph(nodeId: string) {
		if (!projectId) return;
		const sp = new URLSearchParams({ focus: nodeId });
		navigate(`/projects/${projectId}/graph?${sp.toString()}`);
	}

	/**
	 * Continue: pre-load the palette with this thread (Q+A history)
	 * and the current ask as the "branched-from" reference, then open
	 * it. The palette handles the rest — composer focus, submit,
	 * threading. Communication via a window CustomEvent the AppShell
	 * listens on; no global store needed for one cross-component
	 * trigger.
	 */
	function continueThread() {
		if (!question || !result?.answer) return;
		const nextPrior = [
			...thread,
			{ question, answer: result.answer },
		].slice(-3);
		window.dispatchEvent(
			new CustomEvent("renketsu:open-palette", {
				detail: {
					priorTurns: nextPrior,
					branchedFrom: { question, jobId },
				},
			}),
		);
	}

	const canvas: CanvasPlan | null = result
		? validateCanvas(result.canvas) ?? defaultCanvas(result)
		: null;

	return (
		<div className="h-full overflow-y-auto pt-3 pb-12">
			<div className="max-w-[1100px] mx-auto px-4 space-y-6">
				{/* Top bar — back button. Sends the user wherever they
				    came from; falls back to graph if there's no history. */}
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={() => {
							if (window.history.length > 1) navigate(-1);
							else if (projectId) navigate(`/projects/${projectId}/graph`);
						}}
						title="Back"
						aria-label="Back"
						className="pill pill-icon !w-7 !h-7 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
					>
						<ArrowLeft className="w-3.5 h-3.5" />
					</button>
					<span className="text-[11px] text-[var(--color-text-dim)] uppercase tracking-wider">
						Thread
					</span>
				</div>

				{/* Prior turns — compact Q/A. The current turn is the
				    canvas below. */}
				{thread.length > 0 && (
					<div className="space-y-5">
						{thread.map((t, i) => (
							<PriorTurnCard key={i} turn={t} />
						))}
					</div>
				)}

				{question && (
					<div className="text-[17px] text-[var(--color-text)] leading-[1.4]">
						{question}
					</div>
				)}

				{error && (
					<GlassPanel className="px-4 py-3 text-[13px] text-[var(--color-danger)]">
						{error}
					</GlassPanel>
				)}

				{canvas && (
					<Canvas plan={canvas} onFocusNode={focusNodeInGraph} />
				)}

				{/* Loading skeleton — fires while ?job= is set but the
				    fetch hasn't landed. Without this the page flashes
				    blank between mount and first paint of the canvas. */}
				{jobParam && !canvas && !error && <ThreadSkeleton />}

				{!canvas && !error && !jobParam && (
					<div className="pt-4 text-[12px] text-[var(--color-text-muted)]">
						No ask loaded.{" "}
						<kbd className="px-1.5 py-0.5 rounded-[3px] bg-white/[0.05] border border-[var(--color-border)] mono text-[10px]">
							⌘K
						</kbd>{" "}
						to ask something.
					</div>
				)}

				{/* Continue the thread — small text affordance, routes
				    back through the palette. The palette opens with the
				    thread pre-loaded and a "Following up on" indicator. */}
				{result && question && (
					<button
						type="button"
						onClick={continueThread}
						className="flex items-center gap-2 text-[12px] text-[var(--color-text-dim)] hover:text-[var(--color-text)] transition-colors pt-2"
					>
						<CornerDownRight className="w-3.5 h-3.5" />
						<span>Continue the thread</span>
					</button>
				)}
			</div>
		</div>
	);
}

function ThreadSkeleton() {
	return (
		<div className="space-y-5">
			{/* Question line */}
			<div className="skeleton h-[20px] w-[60%]" />
			{/* Sources strip */}
			<div className="space-y-2">
				<div className="skeleton h-[10px] w-[48px]" />
				<div className="flex gap-2 overflow-hidden">
					<div className="skeleton-card shrink-0 w-[220px] h-[64px]" />
					<div className="skeleton-card shrink-0 w-[220px] h-[64px]" />
					<div className="skeleton-card shrink-0 w-[220px] h-[64px] opacity-60" />
				</div>
			</div>
			{/* Prose */}
			<div className="space-y-2.5">
				<div className="skeleton h-[12px] w-full" />
				<div className="skeleton h-[12px] w-full" />
				<div className="skeleton h-[12px] w-[88%]" />
				<div className="skeleton h-[12px] w-[72%]" />
			</div>
		</div>
	);
}

function PriorTurnCard({ turn }: { turn: PriorTurn }) {
	return (
		<div className="border-l-2 border-white/[0.08] pl-4 space-y-2">
			<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
				Asked
			</div>
			<div className="text-[14px] text-[var(--color-text-muted)] leading-[1.4]">
				{turn.question}
			</div>
			<div className="opacity-80">
				<Prose text={turn.answer} />
			</div>
		</div>
	);
}
